/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../../IEditor";
import { TOAST_TYPE } from "../../toasts/IToast";
import { IntegrationEditorEdit } from "./IntegrationEditorEdit/IntegrationEditorEdit";
import { ErrorTracker } from "@adapptio/lib-components";

export function registerCommands(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const toastSvc = dc.get("toastService");

	commandsSvc.register({
		id: "integrationEditor.createPage.open",
		label: "Create Integration resource",
		args: [
			{
				label: "Base path",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const basePath = args[0] as string;

			viewSvc.open("integrationEditor.create", "newIntegration", { basePath }, true);
		}
	});

	commandsSvc.register({
		id: "integrationEditor.edit.saveResource",
		label: "Save Integration resource",
		handler: async () => {
			if (viewSvc.isViewActive("integrationEditor.edit")) {
				const integrationEditor = viewSvc.getCurrentView() as IntegrationEditorEdit;

				const viewInfo = integrationEditor.getViewInfo();
				const resourceInfo = integrationEditor.getResourceInfo();

				if (!viewInfo.isModified || resourceInfo.isBeingSaved) {
					return;
				}

				const loadingToast = toastSvc.add({
					type: TOAST_TYPE.LOADING,
					message: "Saving integration...",
					isClosable: false
				});

				try {
					await integrationEditor.save();

					toastSvc.add({
						type: TOAST_TYPE.SUCCESS,
						message: "Integration saved",
						timeoutMs: 2500
					});
				} catch (err) {
					const errorTracker = ErrorTracker.getInstance();
					errorTracker.captureException(err);

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: "Couldn't save integration.",
						details: String(err)
					});
				} finally {
					toastSvc.remove(loadingToast);
				}
			}
		}
	});

	commandsSvc.register({
		id: "integrationEditor.edit.undo",
		label: "Undo",
		handler: async () => {
			if (viewSvc.isViewActive("integrationEditor.edit")) {
				const integrationEditor = viewSvc.getCurrentView() as IntegrationEditorEdit;
				integrationEditor.undo();
			}
		}
	});

	commandsSvc.register({
		id: "integrationEditor.edit.redo",
		label: "Redo",
		handler: async () => {
			if (viewSvc.isViewActive("integrationEditor.edit")) {
				const integrationEditor = viewSvc.getCurrentView() as IntegrationEditorEdit;
				integrationEditor.redo();
			}
		}
	});
}
